import { BifrostWeb, type BifrostConfig } from '@seekrtech/bifrost-web'
import { __setAppVersion, __setProxies } from './native'
import { pageVisibleOnce } from './page-visible-internal'
import {
  beginPageLoadingRecovery,
  type PageVisiblePayload,
} from './page-loading-recovery-internal'
import {
  defaultSetSafeAreaInsets,
  type SafeAreaInsets,
} from './page-visible-handlers'
import { createAuthService } from './services/auth'
import { createUserService } from './services/user'
import { createPermissionService } from './services/permission'
import { createGeneralService } from './services/general'
import { createIAPService } from './services/iap'
import { createAdditionalService } from './services/additional'
import { createL10nService } from './services/l10n'
import { createDataService } from './services/data'
import { createAssetsService } from './services/assets'
import { createPaywallService } from './services/paywall'
import { createExperimentService } from './services/experiment'
import { createNotificationService } from './services/notification'

export interface InitializeNativeBridgeOptions {
  bifrost?: BifrostConfig
  appVersion?: string
  onPageVisible?: () => void
  onSetSafeAreaInsets?: (insets: SafeAreaInsets) => void
  /** @internal test-only: inject a stub BifrostWeb */
  _bifrost?: BifrostWeb
}

export interface NativeBridge {
  bifrost: BifrostWeb
  ready: Promise<void>
}

/** Must be called exactly once before any `native.*` call. Throws if called twice. */
export function initializeNativeBridge(
  options: InitializeNativeBridgeOptions = {},
): NativeBridge {
  const bifrost = options._bifrost ?? new BifrostWeb(options.bifrost ?? {})

  __setAppVersion(options.appVersion ?? '0.0.0')

  __setProxies({
    auth: createAuthService(bifrost),
    user: createUserService(bifrost),
    permission: createPermissionService(bifrost),
    general: createGeneralService(bifrost),
    iap: createIAPService(bifrost),
    data: createDataService(bifrost),
    assets: createAssetsService(bifrost),
    paywall: createPaywallService(bifrost),
    experiment: createExperimentService(bifrost),
    notification: createNotificationService(bifrost),
    additional: createAdditionalService(bifrost),
    l10n: createL10nService(bifrost),
  })

  const setSafeAreaInsets =
    options.onSetSafeAreaInsets ?? defaultSetSafeAreaInsets

  bifrost.registerService('GeneralService', {
    pageVisible: (payload?: PageVisiblePayload) => {
      beginPageLoadingRecovery(payload)
      options.onPageVisible?.()
      pageVisibleOnce.done()
    },
    setSafeAreaInsets,
  })

  return { bifrost, ready: bifrost.initialize() }
}
